import { useContext } from 'react';

import { i18nContext, useI18n } from 'context/i18n';
import { useProductSheet } from 'context/product-sheet';
import { SIZE_GUIDE_WITH_MODELS } from 'utils/constants';

import styles from './style.module.css';

type MeasureNnormalProps = {
  type?: string;
  target?: string;
  targetId?: string;
};

export function MeasureNnormal({ type, target, targetId }: MeasureNnormalProps) {
  const { t } = useContext(i18nContext);

  const config = {
    shoes: {
      title: t('ficha.producto', 'how.to.measure', 'How to measure your foot'),
      steps: [
        t('ficha.producto', 'measure.shoe.step.1', 'Trace your foot wearing your sports socks on a paper while standing.'),
        t('ficha.producto', 'measure.shoe.step.2', 'Measure from your longest toe to heel.'),
        t('ficha.producto', 'measure.shoe.step.3', 'Do the same for the other foot and compare measurements with our size chart to get the right size.'),
        t(
          'ficha.producto',
          'measure.shoe.step.4',
          'If the length is between 2 sizes:<br /> For tight fit or narrow feet, go one size down. For loose fit or wide feet, go one size up.',
        ),
      ],
      image: '/assets-new/img/sizeguide/shoe.png',
    },
    shorts: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure your size'),
      steps: [
        t('ficha.producto', 'measure.shorts.step.1', 'Leg inseam, from the crotch to the floor.'),
        t('ficha.producto', 'measure.shorts.step.2', 'Waist width on the seam. '),
      ],
      image: `/assets-new/img/sizeguide/shorts-${target}.png`,
    },
    shirt: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure your size'),
      steps: [
        t('ficha.producto', 'measure.shirt.step.1', 'Chest, around the widest part.'),
        t('ficha.producto', 'measure.shirt.step.2', 'Back length, from neck to hip.'),
      ],
      image: `/assets-new/img/sizeguide/shirt-${targetId}.png`,
    },
    vest: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure your size'),
      steps: [t('ficha.producto', 'measure.vest.step.1', 'Chest, around the widest part.')],
      image: '/assets-new/img/sizeguide/vest-X.png',
    },
  };

  const currentConfig = config[type];

  return (
    <div className={styles.sizeMeasure}>
      <div className={styles.sizeMeasureTitle}>{currentConfig?.title}</div>
      <div className={styles.stepsAndImage}>
        {type !== 'shoes' && <p className={styles.titleSteps}>{t('ficha.producto', 'hold.a.tape.measure', 'Hold a tape to measure:')}</p>}
        <ol className={`${styles.sizeMeasureSteps} ${type === 'shoes' ? styles.shoeMeasureSteps : ''}`}>
          {currentConfig?.steps?.map((step, index) => <li key={`step-${index}`} dangerouslySetInnerHTML={{ __html: step }} />)}
        </ol>
        <img className={`${styles.sizeMeasureImage} ${type === 'shoes' ? styles.shoeImage : ''} `} src={currentConfig?.image} alt="" />
      </div>
    </div>
  );
}

type ConfigObject = {
  title: string;
  subtitle?: string;
  steps: string[];
  image: string;
};

type MeasureCamperProps = {
  type?: string;
  sizeGuideInfo?: string;
};

export const MeasureCamper = ({ type, sizeGuideInfo }: MeasureCamperProps) => {
  const { t, locale } = useContext(i18nContext);
  const shirt = {
    title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
    subtitle: t('ficha.producto', 'measure.subtitle.shirt', 'Hold a tape to measure:'),
    steps: [
      t('ficha.producto', 'measure.shirt.step.1', 'Chest, around the widest part.'),
      t('ficha.producto', 'measure.shirt.step.2', 'Back length, from neck to hip.'),
      t('ficha.producto', 'measure.hoodie.step.3', 'Sleeve, from shoulder to wrist.'),
    ],
    image: '/assets-new/img/sizeguide/shirt.png',
  };
  const shorts = {
    title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
    subtitle: t('ficha.producto', 'measure.subtitle.shorts', 'Hold a tape to measure:'),
    steps: [
      t('ficha.producto', 'measure.shorts.step.1', 'Leg inseam, from the crotch to the floor.'),
      t('ficha.producto', 'measure.shorts.step.2', 'Waist width on the seam. '),
    ],
    image: '/assets-new/img/sizeguide/shorts-X.png',
  };

  const config: Record<string, ConfigObject> = {
    shoes: {
      title: t('ficha.producto', 'how.to.measure.camper', 'How to measure?'),
      subtitle: t('ficha.producto', 'measure.subtitle', 'Measure from the tip of your longest toe to the back of your heel.'),
      steps: [],
      image: '/assets-new/img/sizeguide/campershoev2.png',
    },
    shorts,
    hat: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
      subtitle: t('ficha.producto', 'measure.subtitle.hat', 'Hold a tape to measure:'),
      steps: [t('ficha.producto', 'measure.hat.step.1', 'Head, around the widest part.')],
      image: '/assets-new/img/sizeguide/hat.png',
    },
    shirt,
    hoodie: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
      subtitle: t('ficha.producto', 'measure.subtitle.hoodie', 'Hold a tape to measure:'),
      steps: [
        t('ficha.producto', 'measure.hoodie.step.1', 'Chest, around the widest part.'),
        t('ficha.producto', 'measure.hoodie.step.2', 'Back length, from neck to hip.'),
        t('ficha.producto', 'measure.hoodie.step.3', 'Sleeve, from shoulder to wrist.'),
      ],
      image: '/assets-new/img/sizeguide/shirt.png',
    },
    vest: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure your size'),
      subtitle: t('ficha.producto', 'measure.subtitle.shirt', 'Hold a tape to measure:'),
      steps: [t('ficha.producto', 'measure.vest.step.1', 'Chest, around the widest part.')],
      image: '/assets-new/img/sizeguide/vest-X.png',
    },
    scarf: {
      title: t('ficha.producto', 'how.to.measure.size'),
      steps: [t('ficha.producto', 'measure.scarf.step.1'), t('ficha.producto', 'measure.scarf.step.2')],
      image: '/assets-new/img/sizeguide/scarf.png',
    },
  };
  config['shirt jacket'] = shirt;
  config.jacket = shirt;
  config.trousers = shorts;
  const extraInfo = {
    steps: [
      {
        title: t('ficha.producto', 'it.doesnt.fit', "It doesn't fit?"),
        content: t('ficha.producto', 'no.problem.return', 'No problem, find information about Returns here. Free returns in stores.'),
      },
      {
        title: t('ficha.producto', 'still.have.doubts', 'Still have doubts about size?'),
        content: t('ficha.producto', 'help.live.chat', 'Our agents will be happy to help you in live chat.'),
      },
    ],
  };

  const currentConfig = config[type];
  const howToMeasure =
    type === 'shoes' ?
      <div className={styles.howToMeasureShoe}>
        <img src={currentConfig.image} alt="" />
        <h3>{currentConfig?.title}</h3>
        <p>{currentConfig?.subtitle}</p>
      </div>
    : <>
        <h3>{currentConfig?.title}</h3>
        <p className={styles.subtitle}>{currentConfig?.subtitle}</p>
        <ol className={`${styles.sizeMeasureSteps} ${styles.sizeMeasureStepsCamper}`}>
          {currentConfig?.steps?.map((step, index) => <li key={`step-${index}`} dangerouslySetInnerHTML={{ __html: step }} />)}
        </ol>
      </>;
  let sizeGuideMeasureInfo = null;
  if (sizeGuideInfo && sizeGuideInfo.split('<b>').length > 0) {
    const text = sizeGuideInfo.split('<b>').pop();
    const htmlTitleAndDescription = text.split('</b>');
    if (htmlTitleAndDescription.length > 1) {
      sizeGuideMeasureInfo = {
        title: htmlTitleAndDescription[0],
        text: htmlTitleAndDescription[1].replace('<br/>', ''),
      };
    }
  }
  return (
    <div className={styles.camperMeasure}>
      <div className={styles.measureImageTitle}>
        {type !== 'shoes' && type !== 'hat' && (
          <div className={type !== 'hat' && type !== 'shoes' ? `${styles.imageAndLines} ${styles.half}` : styles.imageAndLines}>
            <img src={currentConfig?.image} alt="" />
          </div>
        )}
        <div className={type !== 'hat' && type !== 'shoes' ? `${styles.measureTitle} ${styles.half}` : styles.measureTitle}>
          {sizeGuideMeasureInfo && (
            <>
              <h3>{sizeGuideMeasureInfo?.title}</h3>
              <p className={styles.sizeGuideMeasureInfo} dangerouslySetInnerHTML={{ __html: sizeGuideMeasureInfo?.text }}></p>
            </>
          )}
          {howToMeasure}
          {type === 'hat' && <img src={currentConfig?.image} alt="" />}
          {extraInfo?.steps?.map((step, index) => (
            <div className={styles.alternativeBlock} key={`step-${index}`}>
              <h3 dangerouslySetInnerHTML={{ __html: step.title }} />
              <p dangerouslySetInnerHTML={{ __html: step.content.replace('{LOCALE}', locale) }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export function MeasureCamperlab({ type }: Pick<MeasureCamperProps, 'type'>) {
  const { t, locale } = useI18n();
  const { productSheet } = useProductSheet();
  const sizeGuideInfo = SIZE_GUIDE_WITH_MODELS.includes(productSheet?.code) && productSheet?.description ? productSheet?.description : null;
  const shirt = {
    title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
    subtitle: t('ficha.producto', 'measure.subtitle.shirt', 'Hold a tape to measure:'),
    steps: [
      t('ficha.producto', 'measure.shirt.step.1', 'Chest, around the widest part.'),
      t('ficha.producto', 'measure.shirt.step.2', 'Back length, from neck to hip.'),
      t('ficha.producto', 'measure.hoodie.step.3', 'Sleeve, from shoulder to wrist.'),
    ],
    image: '/assets-new/img/sizeguide/shirt.png',
  };
  const shorts = {
    title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
    subtitle: t('ficha.producto', 'measure.subtitle.shorts', 'Hold a tape to measure:'),
    steps: [
      t('ficha.producto', 'measure.shorts.step.1', 'Leg inseam, from the crotch to the floor.'),
      t('ficha.producto', 'measure.shorts.step.2', 'Waist width on the seam. '),
    ],
    image: '/assets-new/img/sizeguide/shorts-X.png',
  };
  const config = {
    shoes: {
      title: t('ficha.producto', 'how.to.measure.camper', 'How to measure?'),
      subtitle: t('ficha.producto', 'measure.subtitle', 'Measure from the tip of your longest toe to the back of your heel.'),
      steps: [],
      image: '/assets-new/img/sizeguide/campershoev2.png',
    },
    shorts,
    hat: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
      subtitle: t('ficha.producto', 'measure.subtitle.hat', 'Hold a tape to measure:'),
      steps: [t('ficha.producto', 'measure.hat.step.1', 'Head, around the widest part.')],
      image: '/assets-new/img/sizeguide/hat.png',
    },
    shirt,
    hoodie: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure'),
      subtitle: t('ficha.producto', 'measure.subtitle.hoodie', 'Hold a tape to measure:'),
      steps: [
        t('ficha.producto', 'measure.hoodie.step.1', 'Chest, around the widest part.'),
        t('ficha.producto', 'measure.hoodie.step.2', 'Back length, from neck to hip.'),
        t('ficha.producto', 'measure.hoodie.step.3', 'Sleeve, from shoulder to wrist.'),
      ],
      image: '/assets-new/img/sizeguide/shirt.png',
    },
    vest: {
      title: t('ficha.producto', 'how.to.measure.size', 'How to measure your size'),
      subtitle: t('ficha.producto', 'measure.subtitle.shirt', 'Hold a tape to measure:'),
      steps: [t('ficha.producto', 'measure.vest.step.1', 'Chest, around the widest part.')],
      image: '/assets-new/img/sizeguide/vest-X.png',
    },
    jacket: undefined,
    trousers: undefined,
    scarf: {
      title: t('ficha.producto', 'how.to.measure.size'),
      steps: [t('ficha.producto', 'measure.scarf.step.1'), t('ficha.producto', 'measure.scarf.step.2')],
      image: '/assets-new/img/sizeguide/scarf.png',
    },
  };
  config['shirt jacket'] = shirt;
  config.jacket = shirt;
  config.trousers = shorts;
  const extraInfo = {
    steps: [
      {
        title: t('ficha.producto', 'it.doesnt.fit', "It doesn't fit?"),
        content: t('ficha.producto', 'no.problem.return', 'No problem, find information about Returns here. Free returns in stores.'),
      },
      {
        title: t('ficha.producto', 'still.have.doubts', 'Still have doubts about size?'),
        content: t('ficha.producto', 'help.live.chat', 'Our agents will be happy to help you in live chat.'),
      },
    ],
  };
  const currentConfig = config[type];
  const fontSizeClass = 'text-sm lg:text-xs';
  const howToMeasure =
    type === 'shoes' ?
      <div className="">
        <h3 className="mb-0 uppercase">{currentConfig?.title}</h3>
        <p className="mb-2">{currentConfig?.subtitle}</p>
        <img className="mb-5 h-[100px] w-auto" src={currentConfig.image} alt="how to measure shoes" />
      </div>
    : <>
        <h3 className="mb-0 uppercase">{currentConfig?.title}</h3>
        <p className="mb-2">{currentConfig?.subtitle}</p>
        <ol className={''}>{currentConfig?.steps?.map((step, index) => <li key={`step-${index}`} dangerouslySetInnerHTML={{ __html: step }} />)}</ol>
      </>;
  let sizeGuideMeasureInfo = null;
  if (sizeGuideInfo && sizeGuideInfo.split('<b>').length > 0) {
    const text = sizeGuideInfo.split('<b>').pop();
    const htmlTitleAndDescription = text.split('</b>');
    if (htmlTitleAndDescription.length > 1) {
      sizeGuideMeasureInfo = {
        title: htmlTitleAndDescription[0],
        text: htmlTitleAndDescription[1].replace('<br/>', ''),
      };
    }
  }
  return (
    <div className="">
      <div className={fontSizeClass}>
        {howToMeasure}
        {type !== 'shoes' && type !== 'hat' && (
          <div className="mb-5 mt-2">
            <img src={currentConfig?.image} alt="" className="w-2/3" />
          </div>
        )}
        <div>
          {sizeGuideMeasureInfo && (
            <>
              <h3 className="mb-0 uppercase">{sizeGuideMeasureInfo?.title}</h3>
              <p className="mb-2" dangerouslySetInnerHTML={{ __html: sizeGuideMeasureInfo?.text }}></p>
            </>
          )}
          {type === 'hat' && <img src={currentConfig?.image} />}
          {extraInfo?.steps?.map((step, index) => (
            <div className="" key={`step-${index}`}>
              <h3 className="mb-0 uppercase" dangerouslySetInnerHTML={{ __html: step.title }} />
              <p dangerouslySetInnerHTML={{ __html: step.content.replace('{LOCALE}', locale) }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
